/**
 * Route guards — the single place that decides where a request to a page may
 * land. Before this existed, each shell and page did its own token check, which
 * is why some disagreed about where to send a signed-out visitor and why nothing
 * enforced the sign-up questionnaire.
 *
 * The ladder a signed-in user climbs, in order:
 *
 *   no session               → that surface's entrance (/login for students, /staff)
 *   email not verified       → /verify-email
 *   temporary password held  → /change-password
 *   questionnaire open       → /onboarding
 *   otherwise                → the page they asked for
 *
 * The password rung sits above onboarding because a temporary password is only
 * as private as the slip it was written on: until it is replaced, the account
 * must not be usable for anything else — including answering a questionnaire
 * that would write to it.
 *
 * Each stage stops the ladder at a different rung, which is what lets
 * /verify-email and /onboarding be reachable by exactly the people the later
 * rungs would otherwise redirect.
 *
 * This is also where the account is published into the synchronous cache in
 * `lib/auth`, so the ~20 components that read board/grade during render keep
 * working without each fetching it.
 */
import { useEffect, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { LoginSurface } from '@cwy/config/contracts';
import { landingPathForAccount, setAccount, surfaceForRole } from '@/lib/auth';
import { useAccount, useClaims, useMyProfile, useSession } from '@/hooks/useSession';
import { LOGIN_PATH, surfaceForPath } from '@/lib/tenant';
import { NotFoundPage } from '@/pages/NotFoundPage';

export type AuthStage =
  /** Signed in. Nothing more. Used by /verify-email. */
  | 'session'
  /** Signed in with a verified email. Used by /onboarding. */
  | 'verified'
  /** Signed in, verified, and finished onboarding. Everything else. */
  | 'app';

/**
 * A brand-consistent hold while the session resolves.
 *
 * There is always a beat here, and rendering the page underneath meanwhile would
 * flash content the visitor may not be allowed to see.
 */
export function AuthSplash() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-5">
      <div className="flex items-center gap-3 text-ink-soft">
        <img src="/cwy-mark.svg" alt="" width={28} height={28} style={{ borderRadius: '7px' }} />
        <span className="text-sm">One moment…</span>
      </div>
    </div>
  );
}

export function RequireAuth({ stage = 'app' }: { stage?: AuthStage }) {
  const location = useLocation();
  const { data: session, isPending } = useSession();
  const signedIn = Boolean(session?.user);

  // Hooks run unconditionally; `enabled` decides whether they actually fetch.
  const account = useAccount(signedIn);
  // Not fetched for a platform operator: /me/profile is tenant-scoped and would
  // 403 for someone who is deliberately a member of no tenant.
  const profile = useMyProfile(
    signedIn && stage === 'app' && account.data?.platformRole === 'USER',
  );

  // Publish the account for the synchronous readers in lib/auth.
  useEffect(() => {
    if (account.data) setAccount(account.data);
  }, [account.data]);

  if (isPending) return <AuthSplash />;

  if (!signedIn) {
    /*
     * A signed-out visitor is redirected to the entrance for the surface they
     * asked for, and where they asked for is remembered so sign-in can finish the
     * journey rather than dumping them on a console home.
     *
     * Both `/login` and `/staff` are declared as ordinary guest routes, which is
     * what lets this be a plain redirect for every path. It used to need a special
     * case: `/admin` was its own login url as well as its console home, so the box
     * had to render in place there or the redirect would loop onto itself. With
     * administrators signing in at `/staff` that path is gone, and so is the
     * ADMIN sign-in box.
     */
    const loginPath = LOGIN_PATH[surfaceForPath(location.pathname)];
    return (
      <Navigate to={loginPath} state={{ from: location.pathname + location.search }} replace />
    );
  }

  if (stage !== 'session' && !session!.user.emailVerified) {
    // A school-managed student's address is a reserved `.invalid` synthetic one
    // that no verification mail can reach, so holding them here would be a dead
    // end. Their equivalent gate is the forced password change below.
    if (account.data?.email !== null) {
      return <Navigate to="/verify-email" replace />;
    }
  }

  // Every page below the `session` rung reads role/board/grade during render, so
  // hold until the account has landed rather than rendering with an empty cache.
  if (stage !== 'session' && account.isPending) return <AuthSplash />;

  if (stage === 'app') {
    if (account.data?.mustChangePassword) {
      return <Navigate to="/change-password" replace />;
    }

    // A platform operator legitimately has NO membership anywhere — managing
    // tenants is not an action inside one, and giving them a membership would
    // be exactly the "super admin is an implicit admin of every school" the
    // authorization model refuses. They are held to the platform console
    // instead of being sent through the membership and onboarding rungs below,
    // neither of which describes them.
    const isPlatformOperator = Boolean(account.data && account.data.platformRole !== 'USER');
    if (isPlatformOperator) {
      return <Outlet />;
    }

    // No membership in this workspace. Someone following a stale `/school/:slug`
    // link, or a person removed from a school. Offer the workspaces they DO have
    // rather than a bare 403.
    if (account.data && !account.data.membership) {
      return <Navigate to="/choose-workspace" replace />;
    }

    if (profile.isPending) return <AuthSplash />;
    // If the profile read failed, fall back to the account rather than trapping a
    // fully-onboarded student in the questionnaire over a blip.
    const complete = profile.data
      ? profile.data.onboardingComplete
      : account.data
        ? account.data.membership?.role !== 'STUDENT' ||
          Boolean(account.data.membership?.board && account.data.membership?.grade)
        : true;
    if (!complete) return <Navigate to="/onboarding" replace />;
  }

  return <Outlet />;
}

/**
 * A whole console tree, gated to the one surface it belongs to.
 *
 * Each role lives under its own URL prefix — students at the root, teachers under
 * /teacher, administration under /admin — and asking for another one's pages
 * answers "not found". This is presentation, not authorization: it stops a
 * student from landing on a console whose every request would 403, and the API
 * still re-derives the caller's capabilities on each request regardless of which
 * URL the browser is on.
 *
 * It replaced a redirect in each of the three shells. Those disagreed — the
 * student shell sent staff to /teacher while the staff shell sent students to
 * /today — so a person with the wrong role for a page could be volleyed between
 * two shells, and in every case the URL they typed was silently rewritten.
 *
 * Deliberately NOT applied to the handful of routes that serve every role
 * (/settings, /change-password, /choose-workspace). Those are declared outside
 * the gates in the route table rather than special-cased here.
 */
export function RequireSurface({ surface }: { surface: LoginSurface }) {
  const claims = useClaims();

  // RequireAuth already held for the account, so this is populated by the time a
  // child renders. Held anyway rather than deciding on an empty cache.
  if (!claims) return <AuthSplash />;

  if (surfaceForRole(claims.platformRole, claims.role) !== surface) return <NotFoundPage />;
  return <Outlet />;
}

/**
 * The inverse: pages only a signed-OUT visitor should see.
 *
 * An active session never sits on the landing, sign-in or sign-up screens; it
 * goes straight to where that user belongs.
 */
export function GuestOnly({ children }: { children: ReactNode }) {
  const { data: session, isPending } = useSession();
  const account = useAccount(Boolean(session?.user));

  if (isPending) return <AuthSplash />;
  if (session?.user) {
    // Wait for the role before choosing a landing path, or a teacher would be
    // sent to the student home for a frame.
    if (account.isPending) return <AuthSplash />;
    return <Navigate to={landingPathForAccount(account.data)} replace />;
  }
  return <>{children}</>;
}
